import { AppData, Colors, Lightning, Router } from '@lightningjs/sdk';
import Page from 'components/Page';
import { PageId } from 'types/pageId';
import { ChannelLaneItem, ContentHub, VideoLaneItem } from 'services/cwData';
import { getFontFaceFromStyle } from 'support/textUtils';
import { getFocusDepth, getImageTextureObj } from 'support/generalUtils';
import { translate } from 'support/translate';
import LoggedOutBanner from './LoggedOutBanner';
import { Channel, ImageKey, Media, Show, Video } from 'types/api/media';
import DefaultCarousel from 'components/common/carousels/DefaultCarousel';
import {
  CustomTileSpec,
  TileParentContext,
} from 'components/common/tiles/Tile';
import SeeAllTile from 'components/common/tiles/SeeAllTile';
import PortraitTile from 'components/common/tiles/PortraitTile';
import MyStuffContinueTile from 'components/common/tiles/MyStuffContinueTile';
import ChannelTile from 'components/common/tiles/ChannelTile';
import GetStartedSection from './GetStartedSection';
import { SelectItemContext } from 'types/events';
import EmptyListSection from './EmptyListSection';
import { RoutePath, WidgetNames } from 'config/routes';
import { reportItemSelected } from 'services/analytics/reportingServicePage';
import { Config } from 'types/api/config';
import { StaticViewContexts } from 'types/analytics';
import TextLabel from 'components/common/TextLabel';

export type ListProps = {
  carousel: DefaultCarousel;
  items: (Show | Video | Channel)[];
  tile: typeof MyStuffContinueTile | typeof PortraitTile | typeof ChannelTile;
  marginBottom?: number;
};

type SeeAllTileProps = {
  tile: typeof MyStuffContinueTile | typeof PortraitTile | typeof ChannelTile;
  crossIndex: number;
  mainIndex: number;
  rowTitle: string;
  actionArgs: string;
};

export interface MyStuffPageTemplateSpec
  extends Lightning.Component.TemplateSpec {
  pageData: {
    trendingItems: (Show | Video)[];
    continueLaneItems: VideoLaneItem[] | undefined;
    watchlistLaneItems: (Show | Video)[] | undefined;
    favoriteChannelLaneItems: Channel[] | undefined;
  };
  Content: {
    Header: typeof LoggedOutBanner;
    ContinueWatching: {
      Title: typeof TextLabel;
      EmptyMessage: typeof TextLabel;
      List: typeof DefaultCarousel;
    };
    Watchlist: {
      Title: {
        Icon: Lightning.Texture;
        Text: typeof TextLabel;
      };
      ListSection: {
        GetStarted: typeof GetStartedSection;
        EmptyListSection: typeof EmptyListSection;
        List: typeof DefaultCarousel;
      };
    };
    FavoriteChannels: {
      Title: {
        Icon: Lightning.Texture;
        Text: typeof TextLabel;
      };
      ListSection: {
        GetStarted: typeof GetStartedSection;
        EmptyListSection: typeof EmptyListSection;
        List: typeof DefaultCarousel;
      };
    };
  };
}

const CONTENT_WIDTH = 1685;
const CONTENT_X = 176;
const CONTENT_Y = 120;
const CONTENT_Y_SCROLLED_LOGGED_OUT = -600;
const CONTENT_Y_SCROLLED_LOGGED_IN = -440;

const TITLE_FONT_SIZE = 36;
const TITLE_HEIGHT = 42;

const MARGIN_LARGE = 32;
const MARGIN_MEDIUM = 16;
const MARGIN_SMALL = 3;

const LANE_TITLE_HEIGHT = 42;
const LANE_TITLE_BASELINE = LANE_TITLE_HEIGHT - 9;
const LANE_LOCK_ICON_SIZE = 32;
const LANE_HEIGHT = 375;

const ICON_SIZE = 64;

const CONTINUE_TILE_HEIGHT = 292;
const PORTRAIT_TILE_HEIGHT = 375;
const CHANNEL_TILE_HEIGHT = 342;

const CONTINUE_WATCHING_EMPTY_MESSAGE_FONT_SIZE = 24;

export const CONTINUE_WATCHING_MAX_ITEMS = 5;
export const WATCHLIST_MAX_ITEMS = 7;
export const FAVORITE_CHANNELS_MAX_ITEMS = 7;

const LOCK_ICON = getImageTextureObj(
  'static/images/lock-solid-full.svg',
  LANE_LOCK_ICON_SIZE,
  LANE_LOCK_ICON_SIZE,
);

export default class MyStuffPage
  extends Page<MyStuffPageTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<MyStuffPageTemplateSpec>
{
  protected override _pageId = PageId.MY_STUFF;

  private _Content = this.getByRef('Content')!;
  private _Header = this._Content.getByRef('Header')! as LoggedOutBanner;

  private _ContinueWatching = this._Content.getByRef('ContinueWatching')!;
  private _ContinueWatchingTitle = this._ContinueWatching.getByRef('Title')!;
  private _ContinueWatchingEmptyMessage =
    this._ContinueWatching.getByRef('EmptyMessage')!;
  private _ContinueWatchingList = this._ContinueWatching.getByRef('List')!;

  private _Watchlist = this._Content.getByRef('Watchlist')!;
  private _WatchlistLaneTitle = this._Watchlist.getByRef('Title')!;
  private _WatchlistLaneTitleText = this._WatchlistLaneTitle.getByRef('Text')!;
  private _WatchlistListSection = this._Watchlist.getByRef('ListSection')!;
  private _WatchlistGetStarted =
    this._WatchlistListSection.getByRef('GetStarted')!;
  private _WatchlistEmptySection =
    this._WatchlistListSection.getByRef('EmptyListSection')!;
  private _WatchlistList = this._WatchlistListSection.getByRef('List')!;

  private _FavoriteChannels = this._Content.getByRef('FavoriteChannels')!;
  private _FavoriteChannelsTitle = this._FavoriteChannels.getByRef('Title')!;
  private _FavoriteChannelsTitleText =
    this._FavoriteChannelsTitle.getByRef('Text')!;
  private _FavoriteChannelsListSection =
    this._FavoriteChannels.getByRef('ListSection')!;
  private _FavoriteChannelsGetStarted =
    this._FavoriteChannelsListSection.getByRef('GetStarted')!;
  private _FavoriteChannelsEmptySection =
    this._FavoriteChannelsListSection.getByRef('EmptyListSection')!;
  private _FavoriteChannelsList =
    this._FavoriteChannelsListSection.getByRef('List')!;

  private _lastCrossIndex = 0;
  private _lastMainIndex = 0;
  private _lastState = '';
  private isLoggedIn = false;
  private loginConfig:
    | Config['login']['logged_in']
    | Config['login']['logged_out']
    | undefined = undefined;

  get announce() {
    return translate('myStuff.title');
  }

  static override _template(): Lightning.Component.Template<MyStuffPageTemplateSpec> {
    return {
      ...super._template(),
      Content: {
        w: CONTENT_WIDTH,
        x: CONTENT_X,
        y: CONTENT_Y,
        flex: {
          direction: 'column',
        },
        Header: {
          type: LoggedOutBanner,
          flexItem: { marginBottom: MARGIN_LARGE },
          signals: {
            $onHover: '$onHover',
          },
          visible: false,
        },
        ContinueWatching: {
          flex: { direction: 'column' },
          flexItem: { marginBottom: MARGIN_LARGE },

          Title: {
            type: TextLabel,
            h: TITLE_HEIGHT,
            flexItem: { marginBottom: MARGIN_MEDIUM },
            text: {
              text: translate('myStuff.continueWatching'),
              fontSize: TITLE_FONT_SIZE,
              fontFace: getFontFaceFromStyle('regular'),
              offsetY: LANE_TITLE_BASELINE,
            },
          },
          EmptyMessage: {
            type: TextLabel,
            visible: true, // Toggle based on continue watching list
            flexItem: { marginBottom: MARGIN_MEDIUM },
            text: {
              fontSize: CONTINUE_WATCHING_EMPTY_MESSAGE_FONT_SIZE,
              fontFace: getFontFaceFromStyle('regular'),
            },
          },
          List: {
            type: DefaultCarousel,
            listType: MyStuffContinueTile,
            w: CONTENT_WIDTH,
            label: undefined,
            laneIndex: 0,
            enableRequests: true,
            signals: {
              $onHover: '$onHover',
            },
            navBarOffset: CONTENT_X,
          },
        },
        Watchlist: {
          flex: { direction: 'column' },
          flexItem: { marginBottom: MARGIN_LARGE },
          Title: {
            flex: { direction: 'row', alignItems: 'center' },
            flexItem: { marginBottom: MARGIN_MEDIUM },
            Icon: {
              ...LOCK_ICON,
              flexItem: { marginRight: MARGIN_MEDIUM, marginTop: MARGIN_SMALL },
              visible: false,
            },
            Text: {
              type: TextLabel,
              h: LANE_TITLE_HEIGHT,
              text: {
                text: translate('myStuff.watchlist'),
                fontSize: TITLE_FONT_SIZE,
                fontFace: getFontFaceFromStyle('regular'),
                offsetY: LANE_TITLE_BASELINE,
              },
            },
          },
          ListSection: {
            rect: true,
            color: Colors('modalBackground').get(),
            h: LANE_HEIGHT,
            GetStarted: {
              visible: false,
              type: GetStartedSection,
              signals: {
                $onHover: '$onHover',
              },
            },
            EmptyListSection: {
              visible: false,
              type: EmptyListSection,
              flexItem: { marginBottom: MARGIN_MEDIUM },
              signals: {
                $onHover: '$onHover',
              },
            },
            List: {
              type: DefaultCarousel,
              listType: ChannelTile,
              visible: false,
              w: CONTENT_WIDTH,
              label: undefined,
              laneIndex: 1,
              enableRequests: true,
              signals: {
                $onHover: '$onHover',
              },
              navBarOffset: CONTENT_X,
            },
          },
        },
        FavoriteChannels: {
          flex: { direction: 'column' },
          flexItem: { marginBottom: MARGIN_LARGE },
          Title: {
            flex: { direction: 'row', alignItems: 'center' },
            flexItem: { marginBottom: MARGIN_MEDIUM },
            Icon: {
              ...LOCK_ICON,
              flexItem: { marginRight: MARGIN_MEDIUM, marginTop: MARGIN_SMALL },
            },
            Text: {
              type: TextLabel,
              h: LANE_TITLE_HEIGHT,
              text: {
                text: translate('myStuff.favoriteChannels'),
                fontSize: TITLE_FONT_SIZE,
                fontFace: getFontFaceFromStyle('regular'),
                offsetY: LANE_TITLE_BASELINE,
              },
            },
          },
          ListSection: {
            rect: true,
            color: Colors('modalBackground').get(),
            h: LANE_HEIGHT,
            GetStarted: {
              visible: false,
              type: GetStartedSection,
              signals: {
                $onHover: '$onHover',
              },
            },
            EmptyListSection: {
              visible: false,
              type: EmptyListSection,
              action: RoutePath.EPG,
              signals: {
                $onHover: '$onHover',
              },
            },
            List: {
              type: DefaultCarousel,
              listType: PortraitTile,
              visible: false,
              w: CONTENT_WIDTH,
              label: undefined,
              laneIndex: 2,
              enableRequests: true,
              signals: {
                $onHover: '$onHover',
              },
              navBarOffset: CONTENT_X,
            },
          },
        },
      },
    };
  }

  override _setup() {
    super._setup();
    this._setState('ContinueWatching');
  }

  override _getFocused() {
    this.topNavVisible = this._Content.y === CONTENT_Y;
  }

  override _onDataProvided() {
    this.isLoggedIn = !!AppData?.loginRadiusService?.isLoggedIn();
    this.setup(this.isLoggedIn);
  }

  private setup(isLoggedIn: boolean) {
    this.isLoggedIn = isLoggedIn;
    this.loginConfig = isLoggedIn
      ? AppData?.config?.login.logged_in
      : AppData?.config?.login.logged_out;
    this.setUpContinueWatchingList();
    this.setUpWatchList();
    this.setupFavoriteChannelsList();

    this._setState(this._lastState ?? 'ContinueWatching');

    this._Header.patch({
      visible: !this.isLoggedIn,
    });
  }

  private setUpContinueWatchingList() {
    const { trendingItems, continueLaneItems } = this.pageData;

    const hasContinue = continueLaneItems && continueLaneItems.length > 0;

    const emptyDescription = this.loginConfig?.continue_watching.description;
    this._ContinueWatchingEmptyMessage.patch({
      visible: !hasContinue,
      text: { text: emptyDescription },
    });

    this.setupList({
      carousel: this._ContinueWatchingList,
      items: hasContinue ? continueLaneItems : trendingItems,
      tile: hasContinue ? MyStuffContinueTile : PortraitTile,
      marginBottom: hasContinue ? MARGIN_MEDIUM : 0,
    });
  }

  private setUpWatchList() {
    const { watchlistLaneItems } = this.pageData;

    const hasWatchlist = watchlistLaneItems && watchlistLaneItems.length > 0;
    const shouldShowEmptySection = this.isLoggedIn && !hasWatchlist;
    const shouldShowContent = this.isLoggedIn && hasWatchlist;
    const sectionColor = shouldShowContent
      ? Colors('transparent').get()
      : Colors('modalBackground').get();

    this._WatchlistEmptySection.patch({ visible: shouldShowEmptySection });
    const watchlist = this.loginConfig?.watchlist;
    if (shouldShowEmptySection && watchlist) {
      this._WatchlistEmptySection.patch({
        icon: this.getIconObj(watchlist.icon_image_url),
        title: watchlist.title,
        description: watchlist.description,
        buttonLabel: watchlist.cta_button_text,
      });
    }

    this._WatchlistList.patch({ visible: shouldShowContent });
    if (shouldShowContent) {
      this.setupList({
        carousel: this._WatchlistList,
        items: watchlistLaneItems,
        tile: PortraitTile,
      });
    }

    this._WatchlistLaneTitle.patch({
      Icon: {
        visible: !this.isLoggedIn,
      },
    });

    this._WatchlistListSection.patch({
      color: sectionColor,
    });

    this._WatchlistGetStarted.patch({ visible: !this.isLoggedIn });
    if (!this.isLoggedIn && watchlist) {
      this._WatchlistGetStarted.patch({
        icon: this.getIconObj(watchlist.icon_image_url),
        title: watchlist.title,
        description: watchlist.description,
        buttonLabel: watchlist.cta_button_text,
      });
    }
  }

  private setupFavoriteChannelsList() {
    const { favoriteChannelLaneItems } = this.pageData;

    const hasFavoriteChannels =
      favoriteChannelLaneItems && favoriteChannelLaneItems.length > 0;
    const shouldShowEmptySection = this.isLoggedIn && !hasFavoriteChannels;
    const sectionColor =
      this.isLoggedIn && hasFavoriteChannels
        ? Colors('transparent').get()
        : Colors('modalBackground').get();

    this._FavoriteChannelsEmptySection.patch({
      visible: shouldShowEmptySection,
    });
    const favoriteChannels = this.loginConfig?.favorite_channels;
    if (shouldShowEmptySection && favoriteChannels) {
      this._FavoriteChannelsEmptySection.patch({
        icon: this.getIconObj(favoriteChannels.icon_image_url),
        title: favoriteChannels.title,
        description: favoriteChannels.description,
        buttonLabel: favoriteChannels.cta_button_text,
      });
    }

    if (this.isLoggedIn && hasFavoriteChannels) {
      this.setupList({
        carousel: this._FavoriteChannelsList,
        items: favoriteChannelLaneItems,
        tile: ChannelTile,
      });
    }

    this._FavoriteChannelsTitle.patch({
      Icon: {
        visible: !this.isLoggedIn,
      },
    });

    this._FavoriteChannelsListSection.patch({
      color: sectionColor,
    });

    this._FavoriteChannelsGetStarted.patch({ visible: !this.isLoggedIn });
    if (!this.isLoggedIn && favoriteChannels) {
      this._FavoriteChannelsGetStarted.patch({
        icon: this.getIconObj(favoriteChannels.icon_image_url),
        title: favoriteChannels.title,
        description: favoriteChannels.description,
        buttonLabel: favoriteChannels.cta_button_text,
      });
    }

    this._FavoriteChannelsList.patch({
      visible: this.isLoggedIn && hasFavoriteChannels,
    });
  }

  private getIconObj(url: string) {
    return getImageTextureObj(url, ICON_SIZE, ICON_SIZE);
  }

  private setupList(listProps: ListProps) {
    const { carousel, items, tile, marginBottom } = listProps;

    const margin = marginBottom ?? 0;
    let listHeight, imageKey, listItems;

    switch (tile) {
      case ChannelTile:
        listHeight = CHANNEL_TILE_HEIGHT;
        imageKey = 'homeThumbnail';
        listItems = items as ChannelLaneItem[];
        break;
      case MyStuffContinueTile:
        listHeight = CONTINUE_TILE_HEIGHT;
        imageKey = 'thumbnail';
        listItems = items as VideoLaneItem[];
        break;
      case PortraitTile:
      default:
        listHeight = PORTRAIT_TILE_HEIGHT;
        imageKey = 'image_vertical';
        listItems = items as VideoLaneItem[];
        break;
    }

    let maxItems: number;
    let rowTitle = '';
    let actionArgs = '';

    if (carousel === this._ContinueWatchingList) {
      maxItems = CONTINUE_WATCHING_MAX_ITEMS;
      rowTitle = translate('myStuff.continueWatching');
      actionArgs = RoutePath.CONTINUE_WATCHING;
    } else if (carousel === this._WatchlistList) {
      maxItems = WATCHLIST_MAX_ITEMS;
      rowTitle = translate('myStuff.watchlist');
      actionArgs = RoutePath.WATCHLIST;
    } else if (carousel === this._FavoriteChannelsList) {
      maxItems = FAVORITE_CHANNELS_MAX_ITEMS;
      rowTitle = translate('myStuff.favoriteChannels');
      actionArgs = RoutePath.FAVORITE_CHANNELS;
    } else {
      maxItems = Infinity;
    }

    const limitedListItems = listItems.slice(0, maxItems);

    const list = {
      h: listHeight,
      listType: tile,
      itemWidth: tile.width,
      imageKey: imageKey as ImageKey,
      data: limitedListItems,
    };

    carousel.h = list.h;
    carousel.listType = list.listType;
    carousel.itemWidth = list.itemWidth;
    carousel.imageKey = list.imageKey;
    carousel.patch({
      flexItem: {
        marginBottom: margin,
      },
      isNavBarVisible: this.isEnabledWidget(WidgetNames.NAVBAR),
    });

    const carouselItems =
      tile === PortraitTile
        ? (list.data as VideoLaneItem[])
        : (list.data as ChannelLaneItem[]);

    if (list.data && list.data.length > 0) {
      const seeAllTile = this.buildSeeAllTile({
        tile,
        crossIndex: list.data.length,
        mainIndex: carousel.laneIndex,
        rowTitle,
        actionArgs,
      });
      carousel.laneItems = [...carouselItems, seeAllTile];
      carousel.index = this.getSavedIndex(carousel.laneIndex, list.data.length);
    }
  }

  private saveIndex(mainIndex?: number, crossIndex?: number) {
    if (mainIndex === undefined || crossIndex === undefined) return;
    this._lastMainIndex = mainIndex;
    this._lastCrossIndex = crossIndex;
  }

  private getSavedIndex(laneIndex: number, seeAllIndex: number): number {
    if (this._lastMainIndex !== laneIndex) return 0;

    const index = this._lastCrossIndex!;
    this._lastMainIndex = 0;
    this._lastCrossIndex = 0;
    return Math.min(index, seeAllIndex);
  }

  private buildSeeAllTile(props: SeeAllTileProps): CustomTileSpec {
    return {
      type: SeeAllTile,
      isCustomTile: true,
      w: props.tile.highlightWidth,
      h: props.tile.highlightHeight,
      labelText: translate('myStuff.seeAllTile'),
      action: 'navigation',
      actionArgs: props.actionArgs,
      parentContext: {
        crossIndex: props.crossIndex,
        mainIndex: props.mainIndex,
        rowTitle: props.rowTitle,
        isTivoSource: false,
      },
    };
  }

  private _announcerRefresh(component: Lightning.Component) {
    this.fireAncestors(
      '$announcerRefresh',
      getFocusDepth(this.application, component),
    );
  }

  private $onHover(target: Lightning.Component) {
    switch (target) {
      case this._Header.button:
        this._setState('Header');
        break;
      case this._ContinueWatchingList:
        this._setState('ContinueWatching');
        break;
      case this._WatchlistGetStarted.button:
      case this._WatchlistEmptySection.button:
      case this._WatchlistList:
        this._setState('Watchlist');
        break;
      case this._FavoriteChannelsGetStarted.button:
      case this._FavoriteChannelsEmptySection.button:
      case this._FavoriteChannelsList:
        this._setState('FavoriteChannels');
        break;
      default:
        break;
    }
  }

  private shouldScrollDown(scroll: boolean) {
    this.topNavVisible = !scroll;

    this._Content.setSmooth('y', this.getScrolledHeight(scroll));
  }

  private getScrolledHeight(scroll: boolean) {
    return scroll ? this.getContinueWatchingScrollY() : CONTENT_Y;
  }

  private getContinueWatchingScrollY() {
    const scrolledY = this.isLoggedIn
      ? CONTENT_Y_SCROLLED_LOGGED_IN
      : CONTENT_Y_SCROLLED_LOGGED_OUT;
    return this._ContinueWatchingList.h === CONTINUE_TILE_HEIGHT
      ? scrolledY + (PORTRAIT_TILE_HEIGHT - CONTINUE_TILE_HEIGHT)
      : scrolledY;
  }

  private getWatchlistFocusedSection() {
    return this._WatchlistList.visible
      ? this._WatchlistList
      : this._WatchlistEmptySection.visible
      ? this._WatchlistEmptySection
      : this._WatchlistGetStarted;
  }

  private getFavoriteChannelsFocusedSection() {
    return this._FavoriteChannelsList.visible
      ? this._FavoriteChannelsList
      : this._FavoriteChannelsEmptySection.visible
      ? this._FavoriteChannelsEmptySection
      : this._FavoriteChannelsGetStarted;
  }

  private checkResetList(carouselList: DefaultCarousel) {
    carouselList.length > 0 && carouselList.index > 0
      ? (carouselList.index = 0)
      : super._handleBack();
  }

  override _disable() {
    this._setState('Header');
    this.topNavEnable = true;
    super._disable();
  }

  override $onTileSelected(
    data?: Media | ContentHub,
    context: SelectItemContext<TileParentContext> = {},
  ) {
    const mainIndex = context.parentContext?.mainIndex;
    const crossIndex = context.parentContext?.crossIndex;

    const isContinue = mainIndex === this._ContinueWatchingList.laneIndex;
    const isWatchlist = mainIndex === this._WatchlistList.laneIndex;
    const isFavorites = mainIndex === this._FavoriteChannelsList.laneIndex;

    this.saveIndex(mainIndex, crossIndex);

    let rowTitle: string | undefined;
    if (isContinue) {
      rowTitle = this._ContinueWatchingTitle.text?.text;
      context.viewContext = StaticViewContexts.MY_STUFF_CONTINUE_WATCHING;
    } else if (isWatchlist) {
      rowTitle = this._WatchlistLaneTitleText.text?.text;
    } else if (isFavorites) {
      rowTitle = this._FavoriteChannelsTitleText.text?.text;
      context.viewContext = StaticViewContexts.MY_STUFF_FAVORITE_CHANNELS;
    }

    reportItemSelected(
      {
        imageUrl: context?.imageUrl,
        mainIndex,
        crossIndex,
        data,
        rowTitle,
        isTivoTileSource: false,
      },
      this._pageId,
    );

    super.$onTileSelected(data, context);
  }

  private $onSeeAllSelected(context: {
    action: SelectItemContext['action'];
    actionArgs?: unknown;
    parentContext?: TileParentContext;
  }) {
    // Handle See All tile selection
    const { actionArgs, parentContext } = context;
    this.saveIndex(parentContext?.mainIndex, parentContext?.crossIndex);

    // Navigate to the route specified when the tile was created
    if (actionArgs && typeof actionArgs === 'string') {
      Router.navigate(actionArgs);
    }
  }

  static override _states() {
    return [
      class Header extends this {
        override _getFocused() {
          return this._Header;
        }

        override _handleDown() {
          this._setState('ContinueWatching');
        }
      },
      class ContinueWatching extends this {
        override _getFocused() {
          this._announcerRefresh(this._ContinueWatchingList);
          return this._ContinueWatchingList;
        }

        override get announce() {
          return translate('myStuff.continueWatching');
        }

        override $enter() {
          this._lastState = 'ContinueWatching';
        }

        override _handleUp() {
          !this.isLoggedIn ? this._setState('Header') : super._handleUp();
        }

        override _handleDown() {
          this._setState('Watchlist');
        }

        override _handleBack() {
          this.checkResetList(this._ContinueWatchingList);
        }
      },
      class Watchlist extends this {
        override _getFocused() {
          super._getFocused();

          const focusedSection = this.getWatchlistFocusedSection();
          this._announcerRefresh(focusedSection);
          return focusedSection;
        }

        override get announce() {
          return translate('myStuff.watchlist');
        }

        override $enter() {
          this._lastState = 'Watchlist';
          if (AppData?.device.getIsHovering()) return;

          this.shouldScrollDown(true);
        }

        override _handleUp() {
          this.shouldScrollDown(false);
          this._setState('ContinueWatching');
        }

        override _handleDown() {
          this._setState('FavoriteChannels');
        }

        override _handleBack() {
          this.checkResetList(this._WatchlistList);
        }
      },
      class FavoriteChannels extends this {
        override _getFocused() {
          super._getFocused();

          const focusedSection = this.getFavoriteChannelsFocusedSection();
          this._announcerRefresh(focusedSection);
          return focusedSection;
        }

        override get announce() {
          return translate('myStuff.favoriteChannels');
        }

        override $enter() {
          this._lastState = 'FavoriteChannels';
          this.shouldScrollDown(true);
        }

        override _handleUp() {
          this._setState('Watchlist');
        }

        override _handleBack() {
          this.checkResetList(this._FavoriteChannelsList);
        }
      },
    ];
  }
}
